import React, { useState, useEffect } from 'react';
import { settingsAPI, locationsAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';

const SettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchLocations();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (err) {
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAllAdmin();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const getSelectedLocationMapUrl = () => {
    const selectedId = settings?.contact?.selectedLocationId;
    if (!selectedId) return null;
    // selectedLocationId may be a populated object or just an ID string
    if (typeof selectedId === 'object' && selectedId.mapEmbedUrl) {
      return selectedId.mapEmbedUrl;
    }
    const loc = locations.find((l) => l._id === selectedId);
    return loc?.mapEmbedUrl || null;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    try {
      await settingsAPI.update(settings);
      setSuccess('Settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings');
    }
  };

  const handleChange = (path, value) => {
    const keys = path.split('.');
    setSettings((prev) => {
      const newSettings = { ...prev };
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const handleLogoChange = async (type, url) => {
    try {
      await settingsAPI.updateLogo(type, url);
      handleChange(type === 'header' ? 'headerLogo' : type === 'footer' ? 'footerLogo' : 'favicon', url);
      setSuccess('Logo updated successfully');
    } catch (err) {
      setError('Failed to update logo');
    }
  };

  const handleHourChange = (index, field, value) => {
    const newHours = [...(settings.openingHours || [])];
    newHours[index] = { ...newHours[index], [field]: value };
    handleChange('openingHours', newHours);
  };

  if (loading || !settings) return <div>Loading...</div>;

  const defaultDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = settings.openingHours || defaultDays.map(day => ({ day, hours: '', isClosed: false }));

  return (
    <div className="settings-manager">
      <div className="admin-form">
        <h2>Site Settings</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <h3>Logos</h3>
        <ImageUploader
          value={settings.headerLogo}
          onChange={(url) => handleLogoChange('header', url)}
          label="Header Logo"
        />
        <ImageUploader
          value={settings.footerLogo}
          onChange={(url) => handleLogoChange('footer', url)}
          label="Footer Logo"
        />
        <ImageUploader
          value={settings.favicon}
          onChange={(url) => handleLogoChange('favicon', url)}
          label="Favicon"
        />

        <h3>Contact Information</h3>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={settings.contact?.phone || ''}
            onChange={(e) => handleChange('contact.phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={settings.contact?.email || ''}
            onChange={(e) => handleChange('contact.email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Street Address</label>
          <input
            type="text"
            value={settings.contact?.address?.street || ''}
            onChange={(e) => handleChange('contact.address.street', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>City/State</label>
          <input
            type="text"
            value={settings.contact?.address?.city || ''}
            onChange={(e) => handleChange('contact.address.city', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Map Location</label>
          <select
            value={
              typeof settings.contact?.selectedLocationId === 'object'
                ? settings.contact?.selectedLocationId?._id || ''
                : settings.contact?.selectedLocationId || ''
            }
            onChange={(e) => handleChange('contact.selectedLocationId', e.target.value || null)}
          >
            <option value="">-- Select a location --</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.name}
              </option>
            ))}
          </select>
          {getSelectedLocationMapUrl() && (
            <div style={{ marginTop: '10px' }}>
              <iframe
                src={getSelectedLocationMapUrl()}
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen=""
                loading="lazy"
                title="Map Preview"
              ></iframe>
            </div>
          )}
        </div>

        {/* <h3>Opening Hours</h3>
        {hours.map((hour, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={hour.day}
              readOnly
              style={{ width: '120px' }}
            />
            <label>
              <input
                type="checkbox"
                checked={hour.isClosed}
                onChange={(e) => handleHourChange(index, 'isClosed', e.target.checked)}
              />
              Closed
            </label>
            <input
              type="text"
              value={hour.hours}
              onChange={(e) => handleHourChange(index, 'hours', e.target.value)}
              placeholder="e.g., 8am - 7pm"
              disabled={hour.isClosed}
              style={{ flex: 1 }}
            />
          </div>
        ))} */}

        <h3>Social Media</h3>
        <div className="form-group">
          <label>Facebook URL</label>
          <input
            type="url"
            value={settings.socialMedia?.facebook || ''}
            onChange={(e) => handleChange('socialMedia.facebook', e.target.value)}
            placeholder="https://facebook.com/yourpage"
          />
        </div>
        <div className="form-group">
          <label>Instagram URL</label>
          <input
            type="url"
            value={settings.socialMedia?.instagram || ''}
            onChange={(e) => handleChange('socialMedia.instagram', e.target.value)}
            placeholder="https://instagram.com/yourprofile"
          />
        </div>

        <h3>Footer</h3>
        <div className="form-group">
          <label>Copyright Text</label>
          <input
            type="text"
            value={settings.footer?.copyrightText || ''}
            onChange={(e) => handleChange('footer.copyrightText', e.target.value)}
          />
        </div>

        <h3>SEO</h3>
        <div className="form-group">
          <label>Site Title</label>
          <input
            type="text"
            value={settings.seo?.siteTitle || ''}
            onChange={(e) => handleChange('seo.siteTitle', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Site Description</label>
          <textarea
            value={settings.seo?.siteDescription || ''}
            onChange={(e) => handleChange('seo.siteDescription', e.target.value)}
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="dashboard-btn btn-primary" onClick={handleSave}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;
